import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';

import {
  Box, Dialog, IconButton, Switch, Table, TableBody, TableCell, TableHead, TableContainer,
  TableRow, TextField, Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Cancel,
} from '@mui/icons-material';

import { useAuth } from 'src/hooks/useAuth';
import ConfigContext from 'src/contexts/ConfigContext';
import { UserDataType } from 'src/types';
import { axiosHeaders } from 'src/utils/helpers';
import SubmitBtn from 'src/shared/buttons/SubmitBtn';
import CancelBtn from 'src/shared/buttons/CancelBtn';
import styles from './style';
import theme from 'src/themeNew';

interface PropTypes {
  open: boolean;
  onClose: () => void;
  nodeType: string | null;
  nodeId: string | null;
  label: string | null;
  configuration: Record<string, any>;
  isSaving?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfigurationSave: (nodeId: string, configuration: Record<string, any>) => void;
}

const DocumentReviewComponent = (props: PropTypes) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { API } = useContext(ConfigContext);

  const { open, onClose, nodeType, nodeId, configuration, isSaving = false, onConfigurationSave } = props;

  const [users, setUsers] = useState<Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    name: string;
    willReview: boolean;
    loadPercentage: number;
  }>>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [distributeEqually, setDistributeEqually] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  const getUsers = async () => {
    try {
      setUsersLoading(true);
      const url = `${API.getUsersByCompanies}${encodeURIComponent(user?.companyID || '')}`;
      const resp = await Axios.get(url, axiosHeaders(localStorage.getItem('PROCYS_accessToken')));
      if (resp.data.success && resp.data.data?.length > 0) {
        const filteredUsers = resp.data.data.filter((u: UserDataType) => u.status === 'Active');
        const usersData = filteredUsers.map((userData: any) => ({
          id: userData.id?.toString() || userData.email,
          email: userData.email,
          firstName: userData.firstName || userData.firstname,
          lastName: userData.lastName || userData.lastname,
          name: `${userData.firstName || userData.firstname || ''} ${userData.lastName || userData.lastname || ''}`.trim() || userData.email,
          willReview: false,
          loadPercentage: 0,
        }));
        setUsers(usersData);
      }
      setUsersLoading(false);
    } catch (error) {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (nodeType === 'document_review' && configuration) {
      setDistributeEqually(configuration.distributeEqually || false);
    }
  }, [nodeType, configuration]);

  useEffect(() => {
    if (nodeType === 'document_review' && open) {
      getUsers();
    }
  }, [nodeType, open]);

  useEffect(() => {
    if (configuration?.users && Array.isArray(configuration.users) && users.length > 0) {
      const configUsersMap = new Map(
        configuration.users.map((user: any) => [user.email, user])
      );

      const mergedUsers = users.map((userData: any) => {
        const configUser = configUsersMap.get(userData.email);
        if (configUser) {
          return {
            ...userData,
            willReview: configUser.willReview || false,
            loadPercentage: configUser.loadPercentage || 0,
          };
        }
        return userData;
      });

      setUsers(mergedUsers);
    }
  }, [configuration, users.length]);

  const reviewingUsers = users.filter(user => user.willReview);
  const totalLoadPercentage = reviewingUsers.reduce((sum, user) => sum + user.loadPercentage, 0);
  const hasError = totalLoadPercentage !== 100 && reviewingUsers.length > 0;
  const hasValidReviewSetup = reviewingUsers.length > 0 && totalLoadPercentage === 100;

  const handleSwitchChange = (index: number, checked: boolean) => {
    const newUsers = [...users];
    newUsers[index].willReview = checked;
    if (!checked) {
      newUsers[index].loadPercentage = 0;
    }

    if (distributeEqually) {
      const reviewingUsersAfterChange = newUsers.filter(user => user.willReview);
      if (reviewingUsersAfterChange.length > 0) {
        const equalPercentage = Math.floor(100 / reviewingUsersAfterChange.length);
        const remainder = 100 % reviewingUsersAfterChange.length;

        newUsers.forEach((user, userIndex) => {
          if (user.willReview) {
            const reviewerIndex = reviewingUsersAfterChange.findIndex(ru => ru.email === user.email);
            newUsers[userIndex].loadPercentage = equalPercentage + (reviewerIndex < remainder ? 1 : 0);
          }
        });
      }
    }

    setUsers(newUsers);
  };

  const handleDistributeEquallyChange = (checked: boolean) => {
    setDistributeEqually(checked);
    if (checked) {
      const currentReviewingUsers = users.filter(user => user.willReview);
      if (currentReviewingUsers.length > 0) {
        const equalPercentage = Math.floor(100 / currentReviewingUsers.length);
        const remainder = 100 % currentReviewingUsers.length;

        const newUsers = users.map((user, _index) => {
          if (user.willReview) {
            const reviewerIndex = currentReviewingUsers.findIndex(ru => ru.email === user.email);
            return {
              ...user,
              loadPercentage: equalPercentage + (reviewerIndex < remainder ? 1 : 0)
            };
          }
          return user;
        });
        setUsers(newUsers);
      }
    }
  };

  const handleEditClick = (index: number, currentValue: number) => {
    setEditingIndex(index);
    setEditValue(currentValue.toString());
  };

  const handleSaveEdit = (index: number) => {
    const newValue = parseInt(editValue, 10);
    if (!isNaN(newValue) && newValue >= 0 && newValue <= 100) {
      const newUsers = [...users];
      newUsers[index].loadPercentage = newValue;
      setUsers(newUsers);
    }
    setEditingIndex(null);
    setEditValue('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  const handleKeyPress = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter') {
      handleSaveEdit(index);
    } else if (event.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleDocumentReviewSave = () => {
    if (nodeId) {
      const configuration = {
        users: users,
        distributeEqually: distributeEqually,
        totalLoadPercentage: totalLoadPercentage
      };
      onConfigurationSave(nodeId, configuration);
    }
  };

  return (
    <Dialog
      open={open}
      sx={styles.modal}
      onClose={onClose}
    >
      <Box sx={{ ...styles.container, width: '600px' }}>
        <Box sx={styles.modalHeader}>
          <Box sx={styles.titleContainer}>
            <Typography sx={styles.headerTitle}>
              {t('WORKFLOW_NODE_SETTINGS_REVIEW')}
            </Typography>
            <IconButton sx={styles.closeBtn} onClick={onClose}>
              <CloseIcon sx={styles.closeIcon} />
            </IconButton>
          </Box>
        </Box>
        <Box sx={styles.modalContent}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('WORKFLOW_REVIEW_TABLE_USER')}</TableCell>
                  <TableCell>{t('WORKFLOW_REVIEW_TABLE_WILL_REVIEW')}</TableCell>
                  <TableCell>{t('WORKFLOW_REVIEW_TABLE_LOAD_PERCENTAGE')}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>{t('WORKFLOW_REVIEW_LOADING_USERS')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography>{t('WORKFLOW_REVIEW_NO_USERS')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, idx) => (
                    <TableRow key={user.id || user.email || idx}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>
                        <Switch
                          checked={user.willReview}
                          onChange={(e) => handleSwitchChange(idx, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        {editingIndex === idx ? (
                          <TextField
                            size="small"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleKeyPress(e, idx)}
                            inputProps={{
                              min: 0,
                              max: 100,
                            }}
                            type="number"
                            sx={{ width: '80px' }}
                            autoFocus
                            disabled={!user.willReview}
                          />
                        ) : (
                          `${user.loadPercentage}%`
                        )}
                      </TableCell>
                      <TableCell>
                        {editingIndex === idx ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleSaveEdit(idx)}
                              sx={{ color: 'success.main' }}
                              disabled={!user.willReview}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                              sx={{ color: 'error.main' }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEditClick(idx, user.loadPercentage)}
                              disabled={!user.willReview}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        {hasError && reviewingUsers.length > 0 && (
          <Box sx={styles.errorContainer}>
            <Cancel sx={styles.errorIcon} />
            <Typography>
              {t('WORKFLOW_REVIEW_ERROR_LOAD_PERCENTAGE')}
            </Typography>
          </Box>
        )}
        {reviewingUsers.length === 0 && (
          <Box sx={styles.errorContainer}>
            <Cancel sx={styles.errorIcon} />
            <Typography>
              {t('WORKFLOW_REVIEW_ERROR_NO_REVIEWERS')}
            </Typography>
          </Box>
        )}
        <Box sx={styles.loadDistributeContainerOuter}>
          <Box sx={styles.loadDistributeContainer}>
            <Box sx={styles.equalDistributionContainer}>
              <Typography sx={{ fontSize: '13px', color: theme.palette.text.primary, mr: 1 }}>
                {t('WORKFLOW_REVIEW_DISTRIBUTE_EQUALLY')}
              </Typography>
              <Switch
                checked={distributeEqually}
                onChange={(e) => handleDistributeEquallyChange(e.target.checked)}
                disabled={reviewingUsers.length === 0}
              />
            </Box>
            <Box sx={styles.equalDistributionContainer}>
              <Typography color="success.main" sx={{ fontSize: '13px', mr: 2 }}>
                {t('WORKFLOW_REVIEW_LOAD')}: {totalLoadPercentage}%
              </Typography>
              <Typography color="error.main" sx={{ fontSize: '13px' }}>
                {t('WORKFLOW_REVIEW_PENDING')}: {100 - totalLoadPercentage}%
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={styles.modalFooter}>
          <Box display="flex" justifyContent="flex-end" gap="16px">
            <CancelBtn
              title={t('WORKFLOW_BUTTON_CANCEL')}
              onClick={onClose}
            />
            <SubmitBtn
              title={t('WORKFLOW_BUTTON_SAVE')}
              disabled={!hasValidReviewSetup || isSaving}
              onClick={handleDocumentReviewSave}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export default DocumentReviewComponent;
